<template>
  <div
    class="container px-3"
    :class="{ 'keyboard-open': isKeyboardOpen }"
    ref="containerRef"
  >
    <div class="text-right" v-if="!hideCloseButton">
      <div v-on:click="goBack" class="upper-close pt-1">
        <i class="fa-solid fa-x"></i>
      </div>
    </div>
    <div v-if="hideCloseButton" class="mt-3"></div>
    <div
      class="flex-grow-1 d-flex flex-column align-items-center justify-content-center pt-2"
    >
      <img class="curp-img" :src="idValidationImage" />
      <div class="text-center px-3 mt-4">
        <p class="title">
          {{ t("CL_WITHDRAWAL.IDENTITY_VERIFICATION_TITLE") }}
        </p>
      </div>
      <FourDigitsInput
        @validateDigits="curpInputValue = $event"
        :errorMessage="curpValidationError"
        @focusin="handleFocus()"
        :warningMessage="warningMessage"
        @submit="clickBtn"
        :componentId="'lastFourDigitsValidationId'"
      />
    </div>

    <div class="floating-btn" :class="{ 'keyboard-open': isKeyboardOpen }">
      <Button
        id="btn-continue"
        class="text-start w-100"
        @click="clickBtn"
        :disabled="!curpInputValue"
        :loading="isLoading"
      >
        <div class="text-white text-center my-auto fw-bold">
          <span>{{ t("CONFIRM") }}</span>
        </div>
      </Button>
    </div>
    <div>
      <DownUpModal
        v-if="isModalOpen"
        class="down-up-modal"
        :modalHeaderImage="useAsset('img/credit-line/credit-card/advise.png')"
        :modalHeaderTitle="t('CL_WITHDRAWAL.CURP_LIMIT_ATTEMPTS_REACHED')"
        :modalHeaderSubtitle="
          t('CL_WITHDRAWAL.CURP_LIMIT_ATTEMPTS_REACHED_SUBTITLE')
        "
        :btnText="t('CL_WITHDRAWAL.CURP_LIMIT_ATTEMPTS_REACHED_BUTTON')"
        :btn2Text="t('CL_WITHDRAWAL.CURP_LIMIT_ATTEMPTS_REACHED_BUTTON_2')"
        @modalAction="openWhatsApp()"
        @modalAction2="closeModal"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "@/apps/credit-line/composables/useI18n";
import useAsset from "@/apps/credit-line/helpers/useAsset";
import Button from "@/apps/credit-line/components/Button.vue";
import logAnalyticsEvent from "@/apps/credit-line/services/google-analytics-events.service";
import AnalyticEvents from "@/shared-helpers/google-analytics-events.helper";
import FourDigitsInput from "@/apps/credit-line/components/FourDigitsInput.vue";
import DownUpModal from "@/apps/credit-line/components/DownUpModal.vue";
import { AuthenticationHelper } from "@/apps/credit-line/helpers/authentication.helper";
import { AndroidInterface } from "@/apps/credit-line/services/android.service";
import { validateNationId } from "../../services/kyc";
import { useCreditLineSummary } from "../../hooks/useCreditLineSummary";
import { KycUserFlow } from "../../enums/kycEnums";

declare const Android: AndroidInterface;

const props = defineProps({
  customerName: {
    type: String,
    default: "",
  },
  validationId: {
    type: Number,
    required: true,
  },
  hideCloseButton: {
    type: Boolean,
    default: false,
  },
  validationConfig: {
    type: Object,
    default: () => ({}),
  },
  userFlow: {
    type: String as () => KycUserFlow,
    required: false,
    default: null,
  },
});

const emit = defineEmits(["next", "onValidationFailed"]);

const { t } = useI18n();
const router = useRouter();
const curpInputValue = ref<string>("");
const curpInputError = ref<boolean>(false);
const curpValidationError = ref<string>("");
const warningMessage = ref<string>("");
const isKeyboardOpen = ref<boolean>(false);
const validationAttemptsLeft = ref<number>(3);
const isLoading = ref<boolean>(false);
const isModalOpen = ref<boolean>(false);
const { summary } = useCreditLineSummary();

const handleResize = () => {
  isKeyboardOpen.value = window.innerHeight < screen.height * 0.7;
};

const goBack = () => {
  router.back();
};

const idValidationImage = computed(() => {
  if (props.validationConfig?.image) {
    return props.validationConfig.image;
  }
  return useAsset("img/credit-line/credit-card/kycMexicoCard.png");
});

const openWhatsApp = () => {
  const helpMsg = t("CL_WP_HELP_MSG", {
    deviceTag: AuthenticationHelper.getDeviceTag(),
  });
  const whatsappNumber =
    props.validationConfig.whatsAppNumber || "+525575028669";
  window.open(
    `https://wa.me/${whatsappNumber.replace("+", "")}?text=${helpMsg}`,
    "_system"
  );
};

const hideNavBar = () => {
  if (Android && typeof Android?.hideNav === "function") {
    Android.hideNav();
  }
};

watch(isKeyboardOpen, (newValue) => {
  if (!newValue) {
    hideNavBar();
  }
});

onMounted(() => {
  hideNavBar();
  logAnalyticsEvent(AnalyticEvents.CreditLine.KycCurpScreenView, "Withdrawal");
  window.addEventListener("resize", handleResize);
  handleResize();
});

onUnmounted(() => {
  window.removeEventListener("resize", handleResize);
});

const validateCurpMethod = async (validationId: number) => {
  const { data } = await validateNationId({
    lastFourDigits: curpInputValue.value,
    validationId: validationId,
    creditLineId: summary.value?.creditLineId ?? 0,
    deviceTag: AuthenticationHelper.getDeviceTag(),
  });
  warningMessage.value = "";

  if (data.status === "SUCCESS") {
    emit("next", { curp: curpInputValue.value });
    isLoading.value = false;
    return;
  }

  if (data.status === "GENERAL_ERROR") {
    curpValidationError.value = t("CL_WITHDRAWAL.CURP_VALIDATION_ERROR_1");
    isLoading.value = false;
    return;
  }

  if (data.status === "ERROR_VALIDATION") {
    const attemptsCount = data.attempts;
    const attemptsLeft = 3 - attemptsCount ? 3 - attemptsCount : 0;

    curpValidationError.value = t("CL_WITHDRAWAL.CURP_VALIDATION_ERROR_2", {
      attempts_number: attemptsLeft,
    });
    validationAttemptsLeft.value = attemptsLeft;
    isLoading.value = false;
    nextTick();
    return;
  }

  if (data.status === "ERROR_MAX_LIMIT") {
    isModalOpen.value = true;
    isLoading.value = false;
    return;
  }
};

function closeModal() {
  const isWithdrawalPage = window.location.pathname
    .toLowerCase()
    .includes("withdraw");

  const isDefrostWithdrawalPage = window.location.pathname
    .toLowerCase()
    .includes("withdraw/defrost");

  if (isDefrostWithdrawalPage) {
    Android.showNav();
    router.back();
    return;
  }

  if (isWithdrawalPage) {
    Android.showNav();
    window.location.reload();
    return;
  }

  if (
    props.userFlow === KycUserFlow.creditLineCashWithdrawal ||
    props.userFlow === KycUserFlow.creditLineOnlineWithdrawal
  ) {
    Android.showNav();
    router.push({
      name: "CreditLineWithdrawView",
      query: { reload: "true" },
    });
    return;
  }

  router.back();
}
function handleFocus() {
  if (curpValidationError.value && !warningMessage.value) {
    curpValidationError.value = "";
    warningMessage.value = t("CL_WITHDRAWAL.CURP_VALIDATION_WARNING", {
      attempts_number: validationAttemptsLeft.value,
    });
  }
}

function clickBtn() {
  isLoading.value = true;
  curpInputError.value = false;
  curpValidationError.value = "";

  if (!/^[a-zA-Z0-9]{4}$/.test(curpInputValue.value)) {
    isLoading.value = false;
    curpInputError.value = true;
    return;
  }
  validateCurpMethod(props.validationId);
}
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_constants.scss";
$pj-color-red-opaque: rgba(222, 14, 14, 0.4);

.container {
  height: 100%;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}
.title {
  font-size: 20px;
  line-height: 29px;
  font-weight: 600;
  font-family: Inter;
  color: #000000;
}
.curp-img {
  width: 100%;
}
.info-box {
  padding: 20px;
  font-size: 16px;
  border-radius: 5px;
  color: $pj-color-light-green;
  background-color: $pj-color-lighter-green;
}
.error-box {
  padding: 20px;
  font-size: 16px;
  border-radius: 5px;
  color: $pj-color-red;
  background-color: $pj-color-red-opaque;
}
.floating-btn {
  background-color: white;
  position: fixed;
  padding: 10px 20px;
  left: 0;
  bottom: 0;
  z-index: 200;
  height: 60px;
  width: 100%;
  align-items: center;
}

.floating-btn.keyboard-open {
  transform: translateY(0);
}

.container.keyboard-open {
  padding-bottom: 150px;
}
</style>
